import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { AuthApiClient } from 'api';
import { useAuth } from 'contexts/AuthContext';
import { AuthData } from 'models/Auth/types';

export const useAuthenticatedUser = () => {
  return useQuery({
    queryKey: ['authenticatedUser'],
    queryFn: () => AuthApiClient.getAuthenticatedUser(),
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { saveAuth, setCurrentUser } = useAuth();

  return useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const authData = await AuthApiClient.loginByEmail(email, password);
      const user = await AuthApiClient.getAuthenticatedUser();
      return { authData, user };
    },
    onSuccess: ({ authData, user }) => {
      saveAuth(authData.token);
      setCurrentUser(user);
      queryClient.setQueryData(['authenticatedUser'], user);
      toast({
        title: 'Login successful',
        description: `Welcome back, ${user.userName || user.email}!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Login failed',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });
};

export const useRegister = () => {
  const queryClient = useQueryClient();
  const { saveAuth, setCurrentUser } = useAuth();

  return useMutation({
    mutationFn: async ({ email, username, password }: { email: string; username: string; password: string }) => {
      const authData = await AuthApiClient.registerUser(email, username, password);
      const user = await AuthApiClient.getAuthenticatedUser();
      return { authData, user };
    },
    onSuccess: ({ authData, user }) => {
      saveAuth(authData.token);
      setCurrentUser(user);
      queryClient.setQueryData(['authenticatedUser'], user);
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const { logout } = useAuth();

  return useMutation({
    mutationFn: async () => {
      logout();
    },
    onSuccess: () => {
      queryClient.clear();
    },
  });
};

export const useRefreshToken = () => {
  const queryClient = useQueryClient();
  const { saveAuth } = useAuth();

  return useMutation({
    mutationFn: () => AuthApiClient.refreshAuthToken(),
    onSuccess: (authData: AuthData) => {
      saveAuth(authData.token);
    },
  });
}; 